import React, { useState, useCallback } from 'react';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { useUIStore } from '../../stores/useUIStore';
import { useAdminStore } from '../../stores/useAdminStore';
import { parseSeasonId } from '../../utils/seasonHelpers';
import './AdminFormControl.css';
import './PhotoUpload.css';

interface PhotoUploadProps {
  eventName: string;
  month: string;
  seasonId: string;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({ eventName, month, seasonId }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const { uploadPhoto, getSeasonById } = useSeasonsStore();
  const { showToast } = useUIStore();
  const closeUploadModal = useAdminStore(s => s.closeUploadModal);

  const validateFileType = useCallback((file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showToast('Only image files are allowed. Videos are not supported.', 'error');
      return false;
    }
    return true;
  }, [showToast]);

  const handleFileSelect = useCallback((file: File) => {
    if (!validateFileType(file)) {
      return;
    }
    setSelectedFile(file);
    setCaption('');
  }, [validateFileType]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const file = files[0];
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleConfirmUpload = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);

    try {
      await uploadPhoto(eventName, month, seasonId, selectedFile, caption);
      showToast('Photo uploaded successfully', 'success');
      setSelectedFile(null);
      setCaption('');
      setIsUploading(false);
      closeUploadModal();
    } catch (error) {
      console.error(`Failed to upload ${selectedFile.name}:`, error);
      showToast(`Failed to upload ${selectedFile.name}. Please try again.`, 'error');
      setIsUploading(false);
    }
  }, [selectedFile, uploadPhoto, eventName, month, seasonId, caption, closeUploadModal, showToast]);

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setCaption('');
  }, []);

  const seasonData = getSeasonById(seasonId);
  const parsedSeason = parseSeasonId(seasonId);
  const seasonName = seasonData?.name ?? parsedSeason?.name ?? seasonId;
  const year = seasonData?.year ?? parsedSeason?.year ?? new Date().getFullYear();

  const dropZoneClasses = ['drop-zone'];
  if (isDragging) {
    dropZoneClasses.push('dragging');
  }
  if (isUploading) {
    dropZoneClasses.push('uploading');
  }
  const dropZoneClassName = dropZoneClasses.join(' ');

  let dropPrompt: React.ReactNode;
  if (isUploading) {
    dropPrompt = <div>📤 Uploading photo...</div>;
  } else if (selectedFile) {
    dropPrompt = <div>📷 Drop a new photo to replace, or click to choose</div>;
  } else {
    dropPrompt = (
      <>
        <div>📸 Drop a photo here (images only)</div>
        <div>or</div>
      </>
    );
  }

  return (
    <div className="photo-upload-section">
      <h3>📸 Upload Photo</h3>

      {/* Event metadata display */}
      <div className="event-metadata-display">
        <h4>Event Details</h4>
        <div className="event-metadata-grid">
          <div><strong>Event:</strong> {eventName}</div>
          <div><strong>Month:</strong> {month}</div>
          <div><strong>Year:</strong> {year}</div>
          <div><strong>Season:</strong> {seasonName}</div>
        </div>
      </div>

      {/* Selected file container */}
      {selectedFile && (
        <div className="selected-file-container">
          <h4>Selected Photo</h4>

          {/* File info display */}
          <div className="file-info-display">
            <span className="file-icon">📷</span>
            <div className="file-details">
              <div className="file-name">{selectedFile.name}</div>
            </div>
          </div>

          {/* Caption for this specific photo */}
          <div className="caption-section">
            <label htmlFor="photoCaption">Caption for this photo (optional):</label>
            <input
              id="photoCaption"
              type="text"
              className="admin-form-control photo-caption-input"
              placeholder="Enter a caption for this photo..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={isUploading}
              data-testid="photo-caption-input"
            />
          </div>

          {/* Action buttons */}
          <div className="upload-actions">
            <button
              type="button"
              className="clear-file-button"
              onClick={handleClearFile}
              disabled={isUploading}
            >
              Clear Photo
            </button>
            <button
              type="button"
              className="confirm-upload-button"
              onClick={handleConfirmUpload}
              disabled={isUploading || !selectedFile}
              data-testid="confirm-upload-button"
            >
              {isUploading ? 'Uploading...' : 'Upload Photo'}
            </button>
          </div>
        </div>
      )}

      {/* File input and drop zone (always visible) */}
      <div
        className={dropZoneClassName}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="photo-upload-dropzone"
      >
        <div className="drop-content">
          {dropPrompt}
          <label className="file-input-label">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              disabled={isUploading}
              hidden
            />
            {selectedFile ? 'Choose Different Photo' : 'Choose Photo'}
          </label>
        </div>
      </div>
    </div>
  );
};
